// src/components/customer-search/UpdateCustomerSegmentDialog.tsx
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import React, { useState } from "react";
import {
  useGetSegmentsQuery,
  useUpdateCustomerSegmentMutation,
} from "../../store/services/customerService";

interface UpdateCustomerSegmentDialogProps {
  open: boolean;
  onClose: () => void;
  customerIds: number[];
}

const UpdateCustomerSegmentDialog: React.FC<
  UpdateCustomerSegmentDialogProps
> = ({ open, onClose, customerIds }) => {
  const [selectedSegment, setSelectedSegment] = useState<string>("");
  const [updateSegment, { isLoading, error }] =
    useUpdateCustomerSegmentMutation();
  const { data: availableSegments = [], isLoading: isLoadingSegments } =
    useGetSegmentsQuery();

  const handleSegmentChange = (event: SelectChangeEvent<string>) => {
    setSelectedSegment(event.target.value);
  };

  const handleSave = async () => {
    if (!selectedSegment) return; // Don't save if nothing is selected

    try {
      await updateSegment({
        CustomerNos: customerIds,
        SegmentNo: selectedSegment,
      }).unwrap();
      onClose(); // Close dialog on success
    } catch (err) {
      console.error("Failed to update segment:", err);
      // Error state is handled by RTK Query `error` object
    }
  };

  const getDialogTitle = () => {
    if (customerIds.length === 1) {
      return "Ändra kundsegment";
    }
    return `Ändra segment för ${customerIds.length} kunder`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      {isLoadingSegments ? (
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      ) : (
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Kunde inte uppdatera segment. Försök igen.
            </Alert>
          )}
          <FormControl fullWidth margin="normal" disabled={isLoading}>
            <InputLabel id="segment-select-label">Nytt segment</InputLabel>
            <Select
              labelId="segment-select-label"
              id="segment-select"
              value={selectedSegment}
              label="Nytt segment"
              onChange={handleSegmentChange}
            >
              {/* Option to remove segment? Maybe add later if needed */}
              {/* <MenuItem value=""><em>Inget segment</em></MenuItem> */}
              {availableSegments.map((segment) => (
                <MenuItem key={segment.SegmentNo} value={segment.SegmentNo}>
                  {segment.SegmentName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Avbryt
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading || !selectedSegment}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          Spara
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateCustomerSegmentDialog;
